import { isSafeUrl } from "@/lib/utils/urlUtils";

const BOLD_WEIGHT_THRESHOLD = 600;

const hasBoldStyle = (element: HTMLElement): boolean => {
  const fontWeightRaw = element.style.fontWeight || "";
  const fontWeight = fontWeightRaw.toLowerCase();

  if (fontWeight === "bold") {
    return true;
  }

  const numericWeight = Number.parseInt(fontWeight, 10);
  return !Number.isNaN(numericWeight) && numericWeight >= BOLD_WEIGHT_THRESHOLD;
};

const hasItalicStyle = (element: HTMLElement): boolean => {
  const fontStyle = element.style.fontStyle || "";
  return fontStyle.toLowerCase() === "italic";
};

const wrapNodes = (tag: string, nodes: Node[]): Node => {
  const wrapper = document.createElement(tag);
  nodes.forEach((node) => {
    wrapper.appendChild(node);
  });
  return wrapper;
};

const sanitizePastedNode = (node: Node): Node[] => {
  if (node.nodeType === Node.TEXT_NODE) {
    return [document.createTextNode(node.textContent || "")];
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const element = node as HTMLElement;
  const tagName = element.tagName.toLowerCase();

  const sanitizeChildren = () =>
    Array.from(element.childNodes).flatMap((child) =>
      sanitizePastedNode(child),
    );

  const wrapWith = (newTag: string): Node[] => {
    const wrapper = document.createElement(newTag);
    sanitizeChildren().forEach((child) => {
      wrapper.appendChild(child);
    });
    return [wrapper];
  };

  switch (tagName) {
    case "script":
    case "style":
    case "meta":
    case "link":
    case "title":
    case "iframe":
    case "object":
    case "embed":
    case "svg":
    case "img":
      return [];
    case "b":
    case "strong":
      return wrapWith("strong");
    case "i":
    case "em":
      return wrapWith("em");
    case "code":
      return wrapWith("code");
    case "pre":
      return wrapWith("pre");
    case "blockquote":
      return wrapWith("blockquote");
    case "br":
      return [document.createElement("br")];
    case "ul":
      return wrapWith("ul");
    case "ol":
      return wrapWith("ol");
    case "li":
      return wrapWith("li");
    case "a": {
      const href = element.getAttribute("href");
      if (!href || !isSafeUrl(href)) {
        return sanitizeChildren();
      }

      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.rel = "noopener noreferrer nofollow";
      anchor.target = "_blank";
      anchor.classList.add(
        "text-blue-600",
        "underline",
        "hover:underline",
        "cursor-pointer",
      );
      sanitizeChildren().forEach((child) => {
        anchor.appendChild(child);
      });
      return [anchor];
    }
    case "p":
    case "div": {
      const block = document.createElement("div");
      sanitizeChildren().forEach((child) => {
        block.appendChild(child);
      });
      return [block];
    }
    case "span": {
      const children = sanitizeChildren();
      let currentNodes: Node[] = children;

      if (hasBoldStyle(element)) {
        currentNodes = [wrapNodes("strong", currentNodes)];
      }

      if (hasItalicStyle(element)) {
        currentNodes = [wrapNodes("em", currentNodes)];
      }

      return currentNodes;
    }
    default:
      return sanitizeChildren();
  }
};

export const sanitizePastedHtml = (html: string): Node[] => {
  if (typeof window === "undefined") {
    return [];
  }

  const parser = new DOMParser();
  const parsedDoc = parser.parseFromString(html, "text/html");
  const body = parsedDoc.body;

  if (!body) {
    return [];
  }

  const sanitizedNodes = Array.from(body.childNodes).flatMap((child) =>
    sanitizePastedNode(child),
  );

  if (sanitizedNodes.length === 0) {
    const plainText = body.textContent || "";
    return [document.createTextNode(plainText)];
  }

  return sanitizedNodes;
};

export const insertNodesAtSelection = (
  nodes: Node[],
  editor: HTMLDivElement | null,
): void => {
  if (nodes.length === 0) {
    return;
  }

  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    if (editor) {
      const fragment = document.createDocumentFragment();
      nodes.forEach((node) => {
        fragment.appendChild(node);
      });
      editor.appendChild(fragment);
    }
    return;
  }

  const range = selection.getRangeAt(0);
  range.deleteContents();

  const fragment = document.createDocumentFragment();
  let lastNode: Node | null = null;
  nodes.forEach((node) => {
    fragment.appendChild(node);
    lastNode = node;
  });
  range.insertNode(fragment);

  if (lastNode) {
    range.setStartAfter(lastNode);
    range.collapse(true);
  }

  selection.removeAllRanges();
  selection.addRange(range);
};
